/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  pointerWithin,
  closestCorners,
  type CollisionDetection,
  type Collision,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { isTeamNode } from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import { resolveDropHint } from '@sportninja/common/features/competition/operations/utils/resolveDropHint'
import type { SlotRow } from '@sportninja/common/features/competition/operations/utils/resolveDropSlot'
import { resolveDropTarget } from '@sportninja/common/features/competition/operations/utils/resolveDropTarget'
import {
  applyPendingMove,
  type PendingMove,
} from '@sportninja/common/features/competition/operations/utils/applyPendingMove'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import { paragraphSmall } from 'src/components/css'
import TreeNodeRow from './TreeNodeRow'

const translate = t as (key: string, options?: unknown) => string

interface OperationsTreeProps {
  nodes: OperationsNode[]
  selectedId: string | null
  expandedIds: Set<string>
  matchedIds: Set<string>
  canEdit: boolean
  isRegistrationOn?: boolean
  onDrop: (activeId: string, overId: string | null, after: boolean) => void
  /** The dropped move, applied to the tree until the refetch lands. */
  pendingMove: PendingMove | null
  pendingIds: Set<string>
  onSelect: (id: string) => void
  onToggle: (id: string) => void
  onAdd: (node: OperationsNode, kind: 'division' | 'team') => void
  onEdit: (node: OperationsNode) => void
  onDelete: (node: OperationsNode) => void
}

/**
 * Resolves what a drag is currently aimed at, following the legacy Setup tab's
 * model: an insertion SLOT between rows, not the row under the cursor.
 *
 * Legacy's tree library computed an insertion point from the pointer's Y
 * position and drew a landing pad there, which is why the gap below a
 * division's last team was reachable. A row-targeting model cannot express that
 * gap — below the last team there is only the row itself or the next division's
 * header.
 *
 * `resolveDropTarget` holds the rule (and its tests); this only feeds it the
 * geometry dnd-kit provides and smuggles the result out through the winning
 * collision, since drag events expose `collisions` but not rects.
 */
const buildCollisionDetection =
  (teamIds: Set<string>): CollisionDetection =>
  (args) => {
    const withinPointer = pointerWithin(args)
    const collisions =
      withinPointer.length > 0 ? withinPointer : closestCorners(args)

    // The dragged row's own centre, not the pointer: the pointer sits on the
    // grip where the row was grabbed, so measuring from it made the indicator
    // lag behind the row the user is actually looking at.
    const draggedCentreY =
      args.collisionRect.top +
      (args.collisionRect.bottom - args.collisionRect.top) / 2

    const { scheduleRows, teamRows } = rowsFrom(args, teamIds)
    // The lifted row still occupies its original slot, so it must not be able
    // to win as its own target — see `resolveDropTarget`.
    const target = resolveDropTarget(
      scheduleRows,
      teamRows,
      draggedCentreY,
      String(args.active.id)
    )
    if (!target) {
      return collisions
    }

    // Drag events expose `collisions` but not the rects, so the slot rides
    // along in the winning collision's `data` — see `slotOf`.
    const match = collisions.find(
      (collision) => String(collision.id) === target.overId
    )
    const winner = {
      id: target.overId,
      data: { ...(match?.data ?? {}), slotAfter: target.after },
    }

    // When the pointer is past the last team — in the container's padding, or
    // over a division header below it — no collision names that row, so the
    // winner is synthesised rather than the drop being lost.
    return [winner, ...collisions]
  }

/** Row geometry split by kind, each in visual order. */
const rowsFrom = (
  args: Parameters<CollisionDetection>[0],
  teamIds: Set<string>
): { scheduleRows: SlotRow[]; teamRows: SlotRow[] } => {
  const scheduleRows: SlotRow[] = []
  const teamRows: SlotRow[] = []

  args.droppableRects.forEach((rect, id) => {
    const key = String(id)
    const row = { id: key, top: rect.top, bottom: rect.bottom }
    if (teamIds.has(key)) {
      teamRows.push(row)
    } else {
      scheduleRows.push(row)
    }
  })

  const byTop = (a: SlotRow, b: SlotRow) => a.top - b.top
  return {
    scheduleRows: scheduleRows.sort(byTop),
    teamRows: teamRows.sort(byTop),
  }
}

/**
 * Rows never move while dragging.
 *
 * Omitting `strategy` does NOT mean "no sorting" — `SortableContext` falls back
 * to `rectSortingStrategy`, which transforms every row to preview the drop. In
 * a tree measured by our own collision detection that creates a feedback loop:
 * the detector picks a target from `droppableRects`, the strategy shifts the
 * rows to accommodate it, the shifted rows change `droppableRects`, and the
 * detector re-reads geometry its own previous decision produced. The target
 * latches — it can only break loose once the pointer travels far enough to
 * overcome the shift, which is a whole row EDGE rather than a midpoint.
 *
 * Returning null keeps the rail static, so the rects the midpoint rule measures
 * are the rows' real positions. The drop indicator is the only feedback, which
 * is what `TreeNodeRow`'s `dropHintStyle` is for.
 */
const noSorting = () => null

/** The slot flag carried through the winning collision's `data`. */
const slotOf = (collisions: Collision[] | null): boolean =>
  Boolean(
    (collisions?.[0]?.data as { slotAfter?: boolean } | undefined)?.slotAfter
  )

/**
 * Flattens the visible tree into rows. Collapsed subtrees are skipped rather
 * than hidden with CSS, so a large competition renders only what is on screen.
 */
const flatten = (
  nodes: OperationsNode[],
  expandedIds: Set<string>,
  depth = 0
): Array<{ node: OperationsNode; depth: number }> =>
  nodes.flatMap((node) => {
    const row = { node, depth }
    const isExpanded = expandedIds.has(node.id)
    return isExpanded && node.children.length > 0
      ? [row, ...flatten(node.children, expandedIds, depth + 1)]
      : [row]
  })

const OperationsTree = ({
  nodes,
  selectedId,
  expandedIds,
  matchedIds,
  canEdit,
  isRegistrationOn,
  onDrop,
  pendingMove,
  pendingIds,
  onSelect,
  onToggle,
  onAdd,
  onEdit,
  onDelete,
}: OperationsTreeProps) => {
  'use memo'

  // The dropped team is relocated in the TREE before flattening, so a
  // cross-division move paints in its new parent immediately. Patching order
  // per-parent at render time could not do that — the arriving team was not in
  // the destination's children yet.
  const rows = flatten(applyPendingMove(nodes, pendingMove), expandedIds)

  // The slot the pointer is in mid-drag — which gap the team would land in.
  const [activeId, setActiveId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const [slotAfter, setSlotAfter] = useState(false)
  const hint = resolveDropHint(nodes, activeId, overId, slotAfter)

  const teamIds = new Set(
    rows.filter(({ node }) => isTeamNode(node)).map(({ node }) => node.id)
  )
  const collisionDetection = buildCollisionDetection(teamIds)

  const sensors = useSensors(
    // A small distance threshold keeps a click on the row from registering as
    // a drag — the rail's rows are also the selection control.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  if (rows.length === 0) {
    return <p css={emptyStyle}>{translate('DiscoverScreen:noResults')}</p>
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={({ active }: DragStartEvent) =>
        setActiveId(String(active.id))
      }
      onDragMove={({ over, collisions }: DragMoveEvent) => {
        // Slot, not row: `over` alone cannot express "below the last team",
        // which is what made the end of a division unreachable.
        setOverId(over ? String(over.id) : null)
        setSlotAfter(slotOf(collisions))
      }}
      onDragCancel={() => {
        setActiveId(null)
        setOverId(null)
        setSlotAfter(false)
      }}
      onDragEnd={({ active, over, collisions }: DragEndEvent) => {
        const after = slotOf(collisions)
        setActiveId(null)
        setOverId(null)
        setSlotAfter(false)
        onDrop(String(active.id), over ? String(over.id) : null, after)
      }}
    >
      {/*
       * The rail is a tree, not a single reorderable list, so no row may shift
       * to preview a drop — `verticalListSortingStrategy` treats every row as
       * one flat list, which made a team dragged toward another division
       * resolve back to its own siblings.
       *
       * `strategy` must be passed EXPLICITLY. Leaving it off falls back to
       * `rectSortingStrategy` rather than to nothing — see `noSorting`.
       */}
      <SortableContext
        items={rows.map(({ node }) => node.id)}
        strategy={noSorting}
      >
        <div
          css={wrapperStyle}
          role='tree'
          aria-label={translate('common:competition')}
        >
          {rows.map(({ node, depth }) => (
            <TreeNodeRow
              key={node.id}
              node={node}
              depth={depth}
              isSelected={node.id === selectedId}
              isExpanded={expandedIds.has(node.id)}
              isMatch={matchedIds.has(node.id)}
              hasChildren={node.children.length > 0}
              canEdit={canEdit}
              isRegistrationOn={isRegistrationOn}
              isDragDisabled={pendingIds.has(node.id)}
              dropHint={node.id === overId ? hint : null}
              onSelect={onSelect}
              onToggle={onToggle}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

const wrapperStyle = css`
  flex: 1;
  /* A flex item's min-height defaults to auto, so without this the wrapper
     refuses to shrink below its rows and overflow: auto never engages. */
  min-height: 0;
  overflow: auto;
  padding: 6px 0 14px;
`

const emptyStyle = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_100};
  padding: 20px 14px;
  margin: 0;
  text-align: center;
`

export default OperationsTree
